import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useState, useEffect } from 'react';
import { getFeatureFlags } from '@/apis/feature-flags';

// Define all feature flag names as constants to avoid typos
export const FEATURE_FLAGS = {
  ENABLE_AI_FEATURES: 'enable_ai_features',
  ENABLE_SIFT: 'enable_sift',
  ENABLE_AGENT_CHAT: 'enable_agent_chat',
  ENABLE_MAGIC_FILL: 'enable_magic_fill',
  FETCH_PROGRAMS_FROM_API: 'fetch_programs_from_api',
  ENABLE_HEALTH_SNAPSHOT: 'enable_health_snapshot',
  ENABLE_DISCOUNT_CODE_INPUT: 'enable_discount_code_input',
  ENABLE_GIFT_MEMBERSHIP: 'enable_gift_membership',
  ENABLE_MEMBER_MARKETPLACE: 'enable_member_marketplace',
  ENABLE_MEMBER_VIDEOS_TAB: 'enable_member_videos_tab',
  ENABLE_MEMBER_CONSULTATION_VIDEO: 'enable_member_consultation_video',
  ENABLE_MEMBER_WHATS_NEW: 'enable_member_whats_new',
  ENABLE_MEMBER_KNOWLEDGE_BASE: 'enable_member_knowledge_base',
  ENABLE_FAMILY_MANAGEMENT: 'enable_family_management',
  ENABLE_GUT_HEALTH_WITHOUT_NUTRIFIT: 'enable_gut_health_without_nutrifit',
  ENABLE_NEW_THRIVE_CHECK: 'enable_new_thrive_check',
  ENABLE_NOTIFICATIONS: 'enable_notifications',
  ENABLE_MEMBER_CARTS: 'enable_member_carts',
  ENABLE_WEARABLES: 'enable_wearables',
  ENABLE_MEMBER_BENEFITS: 'enable_member_benefits',
  ENABLE_DAILY_LOG: 'enable_daily_log',
  ENABLE_CLIENT_TICKETS: 'enable_client_tickets',
  ENABLE_THYROCARE_NEW_API: 'enable_thyrocare_new_api',
  ENABLE_IN_APP_CHAT: 'enable_in_app_chat',
  ENABLE_IN_APP_CHAT_WEBAPP: 'enable_in_app_chat_webapp',
  ENABLE_MEMBER_CLINICAL_NOTES: 'enable_member_clinical_notes',
  ENABLE_MEMBER_IDEAS: 'enable_member_ideas',
  ENABLE_MEMBER_UPLOAD_AND_PROCESS: 'enable_member_upload_and_process',
  ENABLE_MEMBER_MANUAL_BIOMARKERS: 'enable_member_manual_biomarkers',
  ENABLE_AI_CONTEXT_S3_STORAGE: 'enable_ai_context_s3_storage',
  ENABLE_THRIVE_EARLY_REDEEM: 'enable_thrive_early_redeem',
  ENABLE_HEALTH_CHAT: 'enable_health_chat',
  ENABLE_DOCTOR_ASSIGNMENT: 'enable_doctor_assignment',
  ENABLE_MEMBER_RETEST: 'enable_member_retest',
  // Add more feature flags here
} as const;

export const FEATURE_SETTINGS = {
  MEMBER_HOME_VIDEO_TAGS: 'member_home_video_tags',
  RATE_LIMIT_OVERRIDES: 'rate_limit_overrides',
} as const;

// Type for all feature flags
export type FeatureFlags = {
  [FEATURE_FLAGS.ENABLE_AI_FEATURES]: boolean;
  [FEATURE_FLAGS.ENABLE_SIFT]: boolean;
  [FEATURE_FLAGS.ENABLE_AGENT_CHAT]: boolean;
  [FEATURE_FLAGS.ENABLE_MAGIC_FILL]: boolean;
  [FEATURE_FLAGS.FETCH_PROGRAMS_FROM_API]: boolean;
  [FEATURE_FLAGS.ENABLE_HEALTH_SNAPSHOT]: boolean;
  [FEATURE_FLAGS.ENABLE_DISCOUNT_CODE_INPUT]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_WHATS_NEW]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_KNOWLEDGE_BASE]: boolean;
  [FEATURE_FLAGS.ENABLE_GIFT_MEMBERSHIP]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_VIDEOS_TAB]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_CONSULTATION_VIDEO]: boolean;
  [FEATURE_FLAGS.ENABLE_FAMILY_MANAGEMENT]: boolean;
  [FEATURE_FLAGS.ENABLE_GUT_HEALTH_WITHOUT_NUTRIFIT]: boolean;
  [FEATURE_FLAGS.ENABLE_NEW_THRIVE_CHECK]: boolean;
  [FEATURE_FLAGS.ENABLE_NOTIFICATIONS]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_CARTS]: boolean;
  [FEATURE_FLAGS.ENABLE_WEARABLES]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_BENEFITS]: boolean;
  [FEATURE_FLAGS.ENABLE_DAILY_LOG]: boolean;
  [FEATURE_FLAGS.ENABLE_CLIENT_TICKETS]: boolean;
  [FEATURE_FLAGS.ENABLE_THYROCARE_NEW_API]: boolean;
  [FEATURE_FLAGS.ENABLE_IN_APP_CHAT]: boolean;
  [FEATURE_FLAGS.ENABLE_IN_APP_CHAT_WEBAPP]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_CLINICAL_NOTES]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_IDEAS]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_UPLOAD_AND_PROCESS]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_MANUAL_BIOMARKERS]: boolean;
  [FEATURE_FLAGS.ENABLE_AI_CONTEXT_S3_STORAGE]: boolean;
  [FEATURE_FLAGS.ENABLE_THRIVE_EARLY_REDEEM]: boolean;
  [FEATURE_FLAGS.ENABLE_HEALTH_CHAT]: boolean;
  [FEATURE_FLAGS.ENABLE_DOCTOR_ASSIGNMENT]: boolean;
  [FEATURE_FLAGS.ENABLE_MEMBER_RETEST]: boolean;
  // Add more feature flags here with their types
};

// Helper function to parse environment variable as boolean
const getEnvBoolean = (envVar: string | undefined, defaultValue: boolean): boolean => {
  if (envVar === undefined || envVar === '') return defaultValue;
  return envVar.toLowerCase() === 'true';
};

// Default values for all feature flags
// These are used when the API fails or is not available
// Environment variables override these defaults
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  [FEATURE_FLAGS.ENABLE_AI_FEATURES]: false,
  [FEATURE_FLAGS.ENABLE_SIFT]: true,
  [FEATURE_FLAGS.ENABLE_AGENT_CHAT]: false,
  [FEATURE_FLAGS.ENABLE_MAGIC_FILL]: true, // Enable by default for now
  [FEATURE_FLAGS.FETCH_PROGRAMS_FROM_API]: getEnvBoolean(
    import.meta.env.VITE_FETCH_PROGRAMS_FROM_API,
    false // Default to hardcoded for safety
  ),
  [FEATURE_FLAGS.ENABLE_HEALTH_SNAPSHOT]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_HEALTH_SNAPSHOT,
    true // Enabled by default for all deployments
  ),
  [FEATURE_FLAGS.ENABLE_DISCOUNT_CODE_INPUT]: false, // Disabled by default, backend controls this
  [FEATURE_FLAGS.ENABLE_MEMBER_WHATS_NEW]: false, // Disabled by default for member/CLIENT users
  [FEATURE_FLAGS.ENABLE_MEMBER_KNOWLEDGE_BASE]: false, // Disabled by default for member/CLIENT users
  [FEATURE_FLAGS.ENABLE_GIFT_MEMBERSHIP]: false, // Disabled by default, backend controls this
  [FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE]: false, // Disabled by default, enable via API when ready
  [FEATURE_FLAGS.ENABLE_MEMBER_VIDEOS_TAB]: true, // Enabled by default, backend controls this
  [FEATURE_FLAGS.ENABLE_MEMBER_CONSULTATION_VIDEO]: true, // Enabled by default, backend controls this
  [FEATURE_FLAGS.ENABLE_FAMILY_MANAGEMENT]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_FAMILY_MANAGEMENT,
    false // Disabled by default, enable via env var or API
  ),
  [FEATURE_FLAGS.ENABLE_GUT_HEALTH_WITHOUT_NUTRIFIT]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_GUT_HEALTH_WITHOUT_NUTRIFIT,
    false // Disabled by default, requires NutriFit data or explicit enable
  ),
  [FEATURE_FLAGS.ENABLE_NEW_THRIVE_CHECK]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_NEW_THRIVE_CHECK,
    false // Disabled by default, use old Thrive Check until new one is tested
  ),
  [FEATURE_FLAGS.ENABLE_NOTIFICATIONS]: false, // Disabled by default, enable via API
  [FEATURE_FLAGS.ENABLE_MEMBER_CARTS]: false, // Disabled by default, enable via backoffice
  [FEATURE_FLAGS.ENABLE_WEARABLES]: false, // Disabled by default, enable per-user via admin API
  [FEATURE_FLAGS.ENABLE_MEMBER_BENEFITS]: false, // Disabled by default, enable via backoffice
  [FEATURE_FLAGS.ENABLE_DAILY_LOG]: false, // Disabled by default, enable via backoffice
  [FEATURE_FLAGS.ENABLE_CLIENT_TICKETS]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_CLIENT_TICKETS,
    false // Disabled by default until ticketing UI rollout
  ),
  [FEATURE_FLAGS.ENABLE_THYROCARE_NEW_API]: getEnvBoolean(
    import.meta.env.VITE_THYROCARE_NEW_API_ENABLED,
    false // Default to old Thyrocare payload until explicitly enabled
  ),
  [FEATURE_FLAGS.ENABLE_IN_APP_CHAT]: false, // Mobile app gate, disabled by default
  [FEATURE_FLAGS.ENABLE_IN_APP_CHAT_WEBAPP]: false, // Web app gate, disabled by default
  [FEATURE_FLAGS.ENABLE_MEMBER_CLINICAL_NOTES]: false, // Disabled by default, enable via backoffice
  [FEATURE_FLAGS.ENABLE_MEMBER_IDEAS]: false, // Disabled by default, enable via backoffice
  [FEATURE_FLAGS.ENABLE_MEMBER_UPLOAD_AND_PROCESS]: false, // Disabled by default for CLIENT users, enable via backoffice
  [FEATURE_FLAGS.ENABLE_MEMBER_MANUAL_BIOMARKERS]: false, // Disabled by default for CLIENT users, enable via backoffice
  [FEATURE_FLAGS.ENABLE_AI_CONTEXT_S3_STORAGE]: false, // Disabled by default, enable to use S3 JSONL for AI chat context
  [FEATURE_FLAGS.ENABLE_THRIVE_EARLY_REDEEM]: false, // Gates the mobile Thrive Early redeem flow; enable via backoffice
  // PRD Phase 9: HealthChatPage is now the unconditional /member/chat
  // target; this flag is retained as a deprecated no-op so environments
  // still setting VITE_ENABLE_HEALTH_CHAT don't trip the type system.
  [FEATURE_FLAGS.ENABLE_HEALTH_CHAT]: true,
  [FEATURE_FLAGS.ENABLE_DOCTOR_ASSIGNMENT]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_DOCTOR_ASSIGNMENT,
    false // Default off — flip per environment when ops has seeded pods + doctors.
  ),
  [FEATURE_FLAGS.ENABLE_MEMBER_RETEST]: getEnvBoolean(
    import.meta.env.VITE_ENABLE_MEMBER_RETEST,
    false // Default off — member self-service re-test request (creates a follow-up ticket).
  ),
  // Add more default values here
};

// Hook to get feature flags
export const useFeatureFlags = (shouldFetch = true) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlags = useCallback(async () => {
    if (!shouldFetch) {
      setFlags(DEFAULT_FEATURE_FLAGS);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const session = await fetchAuthSession();
      const accessToken = session?.tokens?.accessToken?.toString();

      // Try to fetch flags - with or without auth token
      // Some flags may be public and accessible without authentication
      const data = await getFeatureFlags(accessToken || '');
      setFlags({ ...DEFAULT_FEATURE_FLAGS, ...data });
    } catch (err) {
      // Only log errors that aren't about missing access tokens
      if (err instanceof Error && !err.message.includes('No access token')) {
        console.error('Error fetching feature flags:', err);
      }
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // Use default values on error
      setFlags(DEFAULT_FEATURE_FLAGS);
    } finally {
      setIsLoading(false);
    }
  }, [shouldFetch]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const refetchFlags = async () => {
    await fetchFlags();
  };

  return { flags, isLoading, error, refetchFlags };
};

// Helper function to check if a feature is enabled
export const isFeatureEnabled = (flags: FeatureFlags, feature: keyof FeatureFlags): boolean => {
  return flags[feature] ?? DEFAULT_FEATURE_FLAGS[feature];
};

export const isFeatureStrictlyEnabled = (
  flags: FeatureFlags,
  feature: keyof FeatureFlags
): boolean => flags[feature] === true;
